import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { PublicHttpUrl } from '../_shared/url.js';
import { AdPlacementSchema, AdTypeSchema } from './enums.js';
import { MediaDTO } from './media.js';
/**
 * Aviso publicitario. Contratos compartidos entre la API y el panel.
 *
 * **El archivo se llama `campania` y no `ad` a propósito.** Los re-exports ESM del barrel hacen
 * que el browser pida cada submódulo por su ruta, y `/ad.js` es una regla de EasyList: con un
 * bloqueador instalado, el pedido se cancela (`ERR_BLOCKED_BY_CLIENT`) y el panel entero queda en
 * pantalla blanca. Por lo mismo la ruta de la API es `/campanias` y no `/ads`. Los identificadores
 * de acá (`AdDTO`, `AdPlacement`) sí conservan el nombre del dominio: viajan dentro del bundle,
 * no como URL.
 *
 * Una ubicación muestra **un** aviso a la vez: si hay dos en circulación y en fecha, gana el de
 * mayor `priority`. Por eso el panel ordena la lista de cada ubicación —el orden **es** la
 * prioridad— y no expone un campo numérico: pedirle a alguien que venda un espacio que además
 * razone con números para saber qué se está viendo es pedirle que se equivoque.
 *
 * Las piezas se **pausan** (`isActive`) o se **borran**: no hay archivado como en categorías.
 * Una campaña que terminó y no se repite no tiene por qué quedar en la lista para siempre, y
 * borrarla no arrastra nada: la imagen sobrevive (`onDelete: SetNull` en `ads.image_id`) y queda
 * disponible para otra pieza.
 */
export const AdDTO = z
    .object({
    id: UuidSchema,
    /** Nombre interno para reconocer la campaña en el panel; no se publica. */
    name: z.string(),
    placement: AdPlacementSchema,
    type: AdTypeSchema,
    /** `IMAGE`: la pieza. `null` en `HTML`, o si la imagen se borró de la biblioteca. */
    image: MediaDTO.nullable(),
    targetUrl: z.string().nullable(),
    htmlSnippet: z.string().nullable(),
    /** `false` = pausado: no se muestra, pero la pieza y sus fechas se conservan. */
    isActive: z.boolean(),
    /** `null` = sin fecha de inicio / sin fecha de fin: el aviso es permanente. */
    startsAt: z.string().datetime().nullable(),
    endsAt: z.string().datetime().nullable(),
    /** Mayor gana. Lo escribe el endpoint de orden; el panel no lo edita a mano. */
    priority: z.number().int().min(0),
    /**
     * Este aviso es el que el lector está viendo en su ubicación, **ahora**. Lo calcula el
     * servidor: la regla del ganador ya vive ahí (la resolución pública), y el reloj de la
     * máquina del editor no decide qué se publica.
     */
    enVivo: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const AD_NAME_MAX = 80;
/** Un tag de red publicitaria son cientos de caracteres; esto da aire y pone techo. */
export const AD_HTML_SNIPPET_MAX = 4000;
/** Techo de piezas por ubicación (la resolución pública lee 100 candidatas). */
export const AD_UBICACION_MAX = 100;
/** Lo que le falta a un aviso para poder salir al aire. */
export const AdRequisitoSchema = z.enum(['imagen', 'destino', 'snippet']);
export const AD_REQUISITO_LABEL = {
    imagen: 'Falta la imagen de la pieza',
    destino: 'Falta la dirección a la que lleva el aviso',
    snippet: 'Falta el código del aviso',
};
/** El campo del formulario al que corresponde cada requisito. */
export const AD_REQUISITO_CAMPO = {
    imagen: 'imageId',
    destino: 'targetUrl',
    snippet: 'htmlSnippet',
};
/**
 * Requisitos sin cumplir.
 *
 * Vive acá porque lo usan tres puntas: el `superRefine` del alta, el servicio (sobre el estado
 * **mezclado**, que es lo único que un PATCH permite validar) y el formulario del panel. Si
 * fueran tres implementaciones, el panel diría que está todo bien y el botón devolvería un 422.
 */
export function adRequisitosPendientes(input) {
    const faltan = [];
    if (input.type === 'IMAGE') {
        if (!input.imageId)
            faltan.push('imagen');
        if (!input.targetUrl)
            faltan.push('destino');
        return faltan;
    }
    if ((input.htmlSnippet ?? '').trim().length === 0)
        faltan.push('snippet');
    return faltan;
}
/** La ventana es válida si falta alguna fecha, o si el fin no es anterior al inicio. */
export function adVentanaValida(startsAt, endsAt) {
    if (!startsAt || !endsAt)
        return true;
    return new Date(endsAt).getTime() >= new Date(startsAt).getTime();
}
/**
 * En circulación y en fecha. `null` en las fechas = permanente.
 *
 * Es la **misma** regla que el `where` con el que la API resuelve los avisos del sitio público;
 * si se cambia una, se cambia la otra (hay un test que las compara).
 */
export function adVigente(ad, ahora = new Date()) {
    if (!ad.isActive)
        return false;
    const t = ahora.getTime();
    if (ad.startsAt && new Date(ad.startsAt).getTime() > t)
        return false;
    if (ad.endsAt && new Date(ad.endsAt).getTime() < t)
        return false;
    return true;
}
const CAMPOS_AD = {
    name: z.string().trim().min(1).max(AD_NAME_MAX),
    placement: AdPlacementSchema,
    imageId: UuidSchema.nullable().optional(),
    // `PublicHttpUrl` y no `z.string().url()`: el destino de un aviso lo escribe un tercero que
    // vendió el espacio, y termina en un href del sitio público.
    targetUrl: PublicHttpUrl.nullable().optional(),
    htmlSnippet: z.string().trim().min(1).max(AD_HTML_SNIPPET_MAX).nullable().optional(),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
};
/** El mensaje del issue de ventana, compartido por el alta y la edición. */
const VENTANA_INVALIDA_MSG = 'La fecha de fin no puede ser anterior a la de inicio';
export const CreateAdRequest = z
    .object({
    ...CAMPOS_AD,
    type: AdTypeSchema.default('IMAGE'),
    isActive: z.boolean().default(true),
})
    .strict()
    .superRefine((v, ctx) => {
    const faltan = adRequisitosPendientes({
        type: v.type,
        imageId: v.imageId ?? null,
        targetUrl: v.targetUrl ?? null,
        htmlSnippet: v.htmlSnippet ?? null,
    });
    for (const falta of faltan) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [AD_REQUISITO_CAMPO[falta]],
            message: AD_REQUISITO_LABEL[falta],
        });
    }
    if (!adVentanaValida(v.startsAt ?? null, v.endsAt ?? null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endsAt'], message: VENTANA_INVALIDA_MSG });
    }
});
/**
 * Edición parcial: lo que no viene queda como está, y `null` limpia.
 *
 * El `superRefine` es a propósito **más débil** que el del alta: un PATCH no conoce el `type`
 * guardado, así que "un aviso de imagen necesita imagen y destino" no se puede decidir acá y lo
 * decide el servicio sobre el estado mezclado. Lo que sí se puede decidir acá es la incoherencia
 * del payload —mandar `type: 'HTML'` junto con una imagen— y la ventana cuando vienen las dos
 * fechas.
 */
export const UpdateAdRequest = z
    .object({
    ...CAMPOS_AD,
    name: CAMPOS_AD.name.optional(),
    placement: AdPlacementSchema.optional(),
    type: AdTypeSchema.optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.type === 'HTML' && (v.imageId || v.targetUrl)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type'],
            message: 'Un aviso de código no lleva imagen ni destino',
        });
    }
    if (v.type === 'IMAGE' && v.htmlSnippet) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type'],
            message: 'Un aviso de imagen no lleva código',
        });
    }
    if (!adVentanaValida(v.startsAt ?? null, v.endsAt ?? null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endsAt'], message: VENTANA_INVALIDA_MSG });
    }
});
export const ListAdsQuery = z
    .object({
    placement: AdPlacementSchema.optional(),
    /** Por default trae el inventario completo, incluidas las pausadas y las terminadas. */
    soloEnCirculacion: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .default(false),
})
    .strict();
/**
 * El orden de una ubicación, **completo**: la lista que llega es la que queda. Si faltan o
 * sobran piezas el server rechaza en vez de acomodar, porque acomodar en silencio significa
 * cambiar sin aviso qué aviso está al aire.
 */
export const OrdenarAdsRequest = z
    .object({
    placement: AdPlacementSchema,
    /** De mayor a menor prioridad: el primero es el que sale al aire. */
    ids: z.array(UuidSchema).max(AD_UBICACION_MAX),
})
    .strict();
/** Códigos de error del dominio que el panel trata de forma específica. */
export const AD_ERROR_CODES = {
    notFound: 'AVISO_NO_ENCONTRADO',
    /** Falta imagen/destino (IMAGE) o código (HTML). `details.faltan` los enumera. */
    incompleto: 'AVISO_INCOMPLETO',
    ventanaInvalida: 'AVISO_VENTANA_INVALIDA',
    /** El `imageId` no existe en la biblioteca de este medio. */
    imagenNoEncontrada: 'AVISO_IMAGEN_NO_ENCONTRADA',
    /** El orden recibido no coincide con las piezas de esa ubicación. */
    ordenIncompleto: 'AVISO_ORDEN_INCOMPLETO',
};
